import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { SEO } from "../components/SEO";
import { FadeUp } from "../components/motion";
import { categoryKeywords } from "../lib/seoKeywords";
import CatalogueGrid from "./CatalogueGrid";
import { useLang } from "../i18n/LanguageContext";

// Page d'atterrissage SEO par univers de produits : /categorie/sac-a-provisions, etc.
export default function CategoryPage() {
  const { slug } = useParams();
  const { lang, t, ts } = useLang();
  const [cat, setCat] = useState(null);  // null = chargement, false = introuvable

  useEffect(() => {
    setCat(null);
    api.get("/products/categories")
      .then((r) => setCat(r.data.find((c) => c.slug === slug) || false))
      .catch(() => setCat(false));
  }, [slug]);

  if (cat === false) {
    return (
      <div className="pt-40 pb-28 text-center px-6" data-testid="category-not-found">
        <h1 className="font-serif text-4xl mb-6">{t("category.not_found")}</h1>
        <Link to="/boutique" className="text-gold hover:text-champagne transition-colors duration-300 inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" /> {t("product.back")}
        </Link>
      </div>
    );
  }
  if (!cat) {
    return <div className="flex items-center justify-center py-40"><Loader2 className="w-8 h-8 animate-spin text-gold" /></div>;
  }

  const name = lang === "en" ? cat.name_en : cat.name;
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: lang === "en" ? "Home" : "Accueil", item: "https://mrtrophee.ca/" },
      { "@type": "ListItem", position: 2, name: lang === "en" ? "Shop" : "Achat en ligne", item: "https://mrtrophee.ca/boutique" },
      { "@type": "ListItem", position: 3, name },
    ],
  };

  return (
    <div className="pt-32 pb-28 max-w-7xl mx-auto px-6 lg:px-10" data-testid="category-page">
      <SEO
        title={`${name} | Mr Trophée`}
        description={ts("category.seo_desc", { name: name.toLowerCase(), n: cat.count })}
        path={`/categorie/${slug}`}
        jsonLd={breadcrumbLd}
        keywords={categoryKeywords(slug, lang)}
      />
      <FadeUp>
        <p className="text-gold tracking-[0.3em] uppercase text-xs mb-4">{t("category.kicker")}</p>
        <h1 className="font-serif text-4xl md:text-5xl leading-tight mb-4" data-testid="category-title">{name}</h1>
        <p className="text-ivory/60 text-sm leading-relaxed max-w-2xl mb-10" data-testid="category-intro">
          {t("category.intro", { name: name.toLowerCase(), n: cat.count })}
        </p>
      </FadeUp>
      <CatalogueGrid fixedCategory={cat.name} />
    </div>
  );
}
